import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../../components/UI-Elements/DashBoardDonutCard';
import { OverlayDisplayDiv } from '../../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { useInventoryReports } from '../../../hooks/useInventoryReports';
import { IInventoryReportType } from '../../../sp-types/InventoryReportType';
import { DEFAULT_COLOR } from '../../../sp-types/InventoryType';
import { months } from '../../../utils/MonthYear/monthYear';
import { AddMaterialPopup } from '../../Inventory/inventory-components/AddMaterialPopup';
import {
  getEntriesFiltered,
  invReportsToStackedBar,
  MULTIPLE_MATS,
  NO_DATA,
  priceChips,
  reportsToSummaryMap,
  terminalChips,
  valChips,
} from '../dashboardUtils';
import { StackedBarChartElement } from '../../../sp-types/dashboardTypes';
import { StackedBarChart } from '../StackedBarDoughnutChartCard';
import { useCurrencyRates } from '../../../hooks/useCurrencyRates';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import { formatToShortString } from '../../../utils/FormatValue';
import { ToggleChips } from './ToggleChips';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const makeDateValue = (month: number, year: number, value: number) => ({ date: { year, month }, value });

export interface ILinesChartCard {
  inventoryReports: IInventoryReportType[] | undefined | null;
  selectedYear: number;
  selectedMaterial?: string;
  refetchData?: (_accountId: string, _materialIds: string[]) => void; // Need to delete
  colorsMap: Record<string, string>;
  terminals: string;
  isTerminals: boolean;
  setIsTerminals: (_isTerminals: boolean) => void;
  showDetails?: () => void;
  setIsQuantity: (_isQuantity: boolean) => void;
  isQuantity?: boolean;
  editable: boolean;
  close?: () => void;
  isPopup?: boolean;
  setMaxVal: (_max: number) => void;
  chartMax: number;
  showProportion: boolean;
}

export const InventoryChartCard: React.FC<ILinesChartCard> = ({
  selectedMaterial,
  inventoryReports,
  selectedYear,
  colorsMap,
  refetchData,
  terminals,
  isTerminals,
  setIsTerminals,
  setIsQuantity,
  isQuantity,
  showDetails,
  editable,
  isPopup = false,
  setMaxVal,
  chartMax,
  showProportion,
}) => {
  const { materialsMap, materials } = useMaterialsStore();
  const { pricesMap, getCurrencyInUSD } = useCurrencyRates();

  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [materialTotals, setMaterialTotals] = useState<IDashboardDonutChartDataSet[]>([]);
  const [barChartData, setBarChartData] = useState<StackedBarChartElement[]>([]);
  const { account } = useAccountStore();
  const { refetch: refetchInventory } = useInventoryReports();

  const [secondaryTitle, setSecondaryTitle] = useState<string>('');
  const [ratioLine, setRatioLine] = useState<string>(
    selectedMaterial && materialsMap[selectedMaterial]?.hasRatio ? 'Ratio - 0mt' : '',
  );
  const [isActualPrice, setIsActualPrice] = React.useState(false);

  useEffect(() => {
    if (inventoryReports) {
      const isGeneral = selectedMaterial === GENERAL_ID;

      const materialReportsMap = reportsToSummaryMap(
        inventoryReports,
        selectedYear,
        materialsMap,
        pricesMap,
        isActualPrice,
        getCurrencyInUSD,
      );
      const materialsEntries = getEntriesFiltered(materialReportsMap, isGeneral ? undefined : selectedMaterial);

      setMaterialTotals(
        materialsEntries.map(
          ([materialId, { lastPrice: sumPrice, quantity, monthVal, unit, ratio }]): IDashboardDonutChartDataSet => ({
            fieldName: materialsMap[materialId]?.name,
            color: colorsMap[materialId] || DEFAULT_COLOR,
            value: sumPrice,
            ratio,
            valuesArray: months.map((m, i) => makeDateValue(i + 1, selectedYear, monthVal[i + 1]?.price || 0)),
            quantity,
            unit,
          }),
        ),
      );

      const barChartDat = invReportsToStackedBar(materialsEntries, materialsMap, colorsMap, !isGeneral && isQuantity);
      setBarChartData(barChartDat);

      const material = materialReportsMap[selectedMaterial || ''];

      setSecondaryTitle(
        isGeneral
          ? MULTIPLE_MATS
          : material
          ? `${formatToShortString(material.quantity)} ${getUnitObj(material.unit).shortForm}`
          : NO_DATA,
      );

      const ratioRecords = isGeneral
        ? []
        : inventoryReports.filter(({ ratio, materialId }) => ratio && ratio > 0 && materialId === selectedMaterial);
      const ratio = ratioRecords.map(({ quantity }) => quantity).reduce((a, b) => a + b, 0);

      setRatioLine(selectedMaterial && materialsMap[selectedMaterial]?.hasRatio ? `Ratio - ${ratio}mt` : '');
    }
  }, [
    inventoryReports,
    selectedYear,
    selectedMaterial,
    materialsMap,
    colorsMap,
    isQuantity,
    isActualPrice,
    //    getCurrencyInUSD,
  ]);

  const onInvAdded = async (isKeepOpen: boolean) => {
    refetchInventory();
    setShowAddMaterial(isKeepOpen);
    account.id && selectedMaterial && refetchData && refetchData(account.id, [selectedMaterial]);
  };

  return inventoryReports ? (
    <div className="flex  flex-col  h-full bg-white p-3 overflow-auto">
      {showAddMaterial && (
        <OverlayDisplayDiv setDisplayOverlay={setShowAddMaterial} hideClose>
          <AddMaterialPopup materials={materials} cancel={() => setShowAddMaterial(false)} onInvAdded={onInvAdded} />
        </OverlayDisplayDiv>
      )}
      <div className="flex flex-row space-x-[0.75rem] justify-between">
        <h4 className="text-lg">{account.titleOverrides?.['inventory'] || 'Inventory'}</h4>
        {!isPopup && (
          <div className="flex flex-row  items-center  gap-2">
            {editable && (
              <button
                onClick={() => setShowAddMaterial(true)}
                className="text-sm text-blue-600 border-1 px-1 rounded border-blue-600 cursor-pointer "
              >
                <b className="text-base">+</b> Add inventory
              </button>
            )}
            <Link to={'/inventory'} className="cursor-pointer">
              <img className="mb-auto" src="/assets/icons/blue-flat/expand.svg" alt="" />
            </Link>
          </div>
        )}
      </div>

      <div className="flex flex-col flex-1 justify-between pt-4 ">
        <div className="flex flex-col flex-1 items-center h-44 ">
          {inventoryReports ? (
            <DashBoardDonutCard
              dataSet={materialTotals}
              secondaryTitle={secondaryTitle}
              ratioLine={ratioLine}
              cssSize="w-52 h-52"
            />
          ) : (
            <div className="flex w-100 h-100 animation-pulse opacity-20"></div>
          )}
        </div>
        {isPopup && (
          <div className="flex h-16 gap-2 items-center text-sm">
            View by:
            <ToggleChips
              width="5rem"
              selected={isActualPrice ? 1 : 0}
              setSelected={(isAct: number) => setIsActualPrice(isAct === 1)}
              chips={priceChips}
            />
            {!!terminals && !showDetails && (
              <ToggleChips
                width="5rem"
                selected={isTerminals ? 1 : 0}
                setSelected={(isTerm) => setIsTerminals(isTerm === 1)}
                chips={terminalChips}
              />
            )}
            {selectedMaterial !== GENERAL_ID && (
              <ToggleChips
                width="5rem"
                selected={isQuantity ? 1 : 0}
                setSelected={(isQuan) => setIsQuantity(isQuan === 1)}
                chips={valChips}
              />
            )}
          </div>
        )}
        <div className="h-[12.5rem] align-center w-full  ">
          <StackedBarChart
            stackedBarChartData={barChartData}
            terminals={isTerminals ? terminals : ''}
            isGeneral={selectedMaterial === GENERAL_ID}
            unit={
              selectedMaterial !== GENERAL_ID && isQuantity
                ? getUnitObj(materialsMap[selectedMaterial || ''].unit).shortForm + ' '
                : '$'
            }
            {...{ setMaxVal, chartMax, showProportion, firstMonthName: 'Stg' }}
            name="inventory"
          />
        </div>
        {!isPopup && (
          <div className="flex flex-row-reverse justify-between pt-2 w-full text-sm text-blue-600">
            {showDetails && (
              <div
                className="flex scale-90 gap-2 border-1 px-1 py-0.5 rounded border-blue-600 cursor-pointer hover:bg-gray-75 transform hover:scale-100 transition-transform duration-300"
                onClick={showDetails}
              >
                Show details
              </div>
            )}
            {selectedMaterial !== GENERAL_ID && (
              <ToggleChips
                width="5rem"
                selected={isQuantity ? 1 : 0}
                setSelected={(isQuan) => setIsQuantity(isQuan === 1)}
                chips={valChips}
              />
            )}
          </div>
        )}
      </div>
    </div>
  ) : (
    <div>Loading</div>
  );
};
